import axios, {AxiosError, InternalAxiosRequestConfig, isAxiosError, isCancel} from 'axios';
import {flushUserConsolePersist} from './userConsoleLogs';

const REQUEST_ERROR_DEDUP_MS = 5000;
const EVENT_DEDUP_MS = 5000;
const NOISY_LOGOUT_REASONS = new Set([
    'session_already_expired',
    'missing_token',
    'missing_token_poll',
    'missing_token_redirect_login',
    'missing_expiration_poll'
]);

type AxiosConfigWithMeta = InternalAxiosRequestConfig & {metadata?: {startedAt: number}};

type RequestErrorRate = {
    lastLogAt: number;
    suppressed: number;
};

let requestCaptureInstalled = false;
let globalErrorCaptureInstalled = false;
let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;
let logoutLoggedForSession = false;
let lastBoundUserName = '';
let eventSeq = 0;
const requestErrorRate = new Map<string, RequestErrorRate>();
const eventRate = new Map<string, RequestErrorRate>();

function nextSeq() {
    eventSeq += 1;
    return eventSeq;
}

function shouldLogKeyed(map: Map<string, RequestErrorRate>, key: string, windowMs: number): {ok: boolean; suppressed: number} {
    const now = Date.now();
    const previous = map.get(key);
    if (previous && now - previous.lastLogAt < windowMs) {
        previous.suppressed += 1;
        return {ok: false, suppressed: previous.suppressed};
    }
    const suppressed = previous?.suppressed ?? 0;
    map.set(key, {lastLogAt: now, suppressed: 0});
    return {ok: true, suppressed};
}

function readStorageFlag(storage: Storage | undefined, key: string): string | null {
    try {
        return storage?.getItem(key) ?? null;
    } catch {
        return null;
    }
}

function isPublicAuthPage(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    const href = `${window.location.pathname || ''} ${window.location.hash || ''}`;
    return href.includes('/login') ||
        href.includes('forgotpassword') ||
        href.includes('pass/recovery') ||
        href.includes('auth_google') ||
        href.includes('authError/error');
}

function logoutContext() {
    const session = typeof sessionStorage !== 'undefined' ? sessionStorage : undefined;
    const local = typeof localStorage !== 'undefined' ? localStorage : undefined;
    const expiration = readStorageFlag(session, 'expirationTime') || readStorageFlag(local, 'expirationTime');
    const expirationMs = expiration ? Number(expiration) : null;

    return {
        href: typeof window !== 'undefined' ? window.location.href : '',
        online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        hasAuthToken: !!(readStorageFlag(session, 'authToken') || readStorageFlag(local, 'authToken')),
        isLoggedOutFlag: readStorageFlag(session, 'isLoggedOut'),
        expirationTime: expiration,
        expiresInMs: expirationMs && Number.isFinite(expirationMs) ? expirationMs - Date.now() : null
    };
}

function compactResponseData(data: unknown): unknown {
    if (data == null) {
        return data;
    }
    if (typeof data === 'string') {
        return data.slice(0, 500);
    }
    if (typeof data !== 'object') {
        return data;
    }

    const payload = data as Record<string, unknown>;
    const compact: Record<string, unknown> = {};
    ['statusCode', 'title', 'message', 'detail', 'errors', 'type'].forEach((key) => {
        if (payload[key] != null) {
            compact[key] = payload[key];
        }
    });
    return Object.keys(compact).length ? compact : undefined;
}

export function isIgnorableRequestError(error: unknown): boolean {
    if (isCancel(error)) {
        return true;
    }
    const axiosError = isAxiosError(error) ? error : null;
    const code = axiosError?.code || (error as {code?: string})?.code;
    const name = axiosError?.name || (error as {name?: string})?.name;
    return code === 'ERR_CANCELED' || name === 'CanceledError' || name === 'AbortError';
}

export function summarizeRequestError(error: unknown) {
    const err = (isAxiosError(error) ? error : error) as AxiosError & {config?: AxiosConfigWithMeta};
    const config = err?.config;
    const response = isAxiosError(error) ? error.response : undefined;
    const startedAt = config?.metadata?.startedAt;
    const url = typeof config?.url === 'string' ? config.url : '';
    const baseURL = typeof config?.baseURL === 'string' ? config.baseURL : '';
    const fullUrl = url.startsWith('http') || !baseURL ? url : `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;

    return {
        method: String(config?.method || 'get').toUpperCase(),
        url: fullUrl || '(unknown)',
        status: response?.status ?? null,
        statusText: response?.statusText ?? null,
        code: err?.code ?? (error as {code?: string})?.code ?? null,
        message: err?.message || (error instanceof Error ? error.message : String(error)),
        durationMs: startedAt ? Date.now() - startedAt : null,
        response: compactResponseData(response?.data),
        online: typeof navigator !== 'undefined' ? navigator.onLine : undefined
    };
}

function requestErrorKey(summary: ReturnType<typeof summarizeRequestError>): string {
    return `${summary.method} ${summary.url} ${summary.status ?? summary.code ?? 'error'}`;
}

export function logRequestError(error: unknown, extra?: Record<string, unknown>) {
    if (isIgnorableRequestError(error)) {
        return;
    }

    const summary = summarizeRequestError(error);
    const key = requestErrorKey(summary);
    const gate = shouldLogKeyed(requestErrorRate, key, REQUEST_ERROR_DEDUP_MS);
    if (!gate.ok) {
        return;
    }
    console.error('[request-error]', {
        seq: nextSeq(),
        ...summary,
        ...(gate.suppressed ? {suppressedSinceLastLog: gate.suppressed} : {}),
        ...extra
    });
}

function summarizeAxiosConfig(config?: AxiosConfigWithMeta | null) {
    const url = typeof config?.url === 'string' ? config.url : '';
    const baseURL = typeof config?.baseURL === 'string' ? config.baseURL : '';
    const fullUrl = url.startsWith('http') || !baseURL ? url : `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
    const startedAt = config?.metadata?.startedAt;
    return {
        method: String(config?.method || 'get').toUpperCase(),
        url: fullUrl || '(unknown)',
        durationMs: startedAt ? Date.now() - startedAt : null
    };
}

export function isApiBusinessError(data: unknown): boolean {
    if (!data || typeof data !== 'object') {
        return false;
    }
    const payload = data as Record<string, unknown>;
    const statusCode = payload.statusCode;
    const message = typeof payload.message === 'string' ? payload.message : '';
    if (message === 'Access Denied') {
        return true;
    }
    return typeof statusCode === 'number' && statusCode !== 200;
}

export function logApiBusinessError(response: {status?: number; data?: unknown; config?: AxiosConfigWithMeta} | null | undefined) {
    if (!response || !isApiBusinessError(response.data)) {
        return;
    }
    const summary = summarizeAxiosConfig(response.config);
    const gate = shouldLogKeyed(requestErrorRate, `${summary.method} ${summary.url} body:${(response.data as any)?.statusCode}`, REQUEST_ERROR_DEDUP_MS);
    if (!gate.ok) {
        return;
    }
    console.error('[api-error]', {
        seq: nextSeq(),
        httpStatus: response.status ?? 200,
        ...summary,
        response: compactResponseData(response.data),
        ...(gate.suppressed ? {suppressedSinceLastLog: gate.suppressed} : {})
    });
}

export function logToast(kind: string, details?: Record<string, unknown>) {
    const gate = shouldLogKeyed(eventRate, `toast:${kind}:${details?.message ?? ''}`, EVENT_DEDUP_MS);
    if (!gate.ok) {
        return;
    }
    console.warn('[toast]', {
        seq: nextSeq(),
        kind,
        ...(gate.suppressed ? {suppressedSinceLastLog: gate.suppressed} : {}),
        ...details
    });
}

export function logLogoutPending(reason: string, details?: Record<string, unknown>) {
    console.warn('[logout-pending]', {
        seq: nextSeq(),
        reason,
        ...logoutContext(),
        ...details
    });
}

export function logLogoutCancelled(reason: string, details?: Record<string, unknown>) {
    console.info('[logout-cancelled]', {
        seq: nextSeq(),
        reason,
        ...logoutContext(),
        ...details
    });
}

export function logHub(hub: string, event: string, details?: Record<string, unknown>) {
    const noisy = event === 'reconnecting' || event === 'disconnected';
    if (noisy) {
        const gate = shouldLogKeyed(eventRate, `hub:${hub}:${event}`, EVENT_DEDUP_MS);
        if (!gate.ok) {
            return;
        }
    }
    const level = event.includes('fail') || event.includes('error') ? 'error' : event.includes('disconnect') || event === 'reconnecting' || event === 'offline' ? 'warn' : 'info';
    console[level]('[hub]', {
        seq: nextSeq(),
        hub,
        event,
        ...details
    });
}

export function logCall(event: string, details?: Record<string, unknown>) {
    const level = event.includes('drop') || event.includes('fail') ? 'error' : 'info';
    console[level]('[call]', {
        seq: nextSeq(),
        event,
        ...details
    });
}

export function logAction(action: string, details?: Record<string, unknown>) {
    console.info('[action]', {
        seq: nextSeq(),
        action,
        href: typeof window !== 'undefined' ? window.location.href : '',
        ...details
    });
}

export function logLogout(reason: string, details?: Record<string, unknown>) {
    if (NOISY_LOGOUT_REASONS.has(reason) && isPublicAuthPage()) {
        return;
    }
    if (logoutLoggedForSession) {
        return;
    }
    logoutLoggedForSession = true;
    console.warn('[logout]', {
        seq: nextSeq(),
        reason,
        ...logoutContext(),
        ...details
    });
    flushUserConsolePersist();
}

export function restoreUserConsoleLogsToStorage() {
    flushUserConsolePersist();
}

export function resetAppEventLogState() {
    logoutLoggedForSession = false;
    requestErrorRate.clear();
    eventRate.clear();
    eventSeq = 0;
}

export function bindAppEventLogStore(store: {getState: () => any; subscribe: (listener: () => void) => () => void}) {
    const sync = () => {
        const userName = store.getState()?.UsersReducer?.userData?.userName;
        const next = typeof userName === 'string' ? userName.trim() : '';
        if (next && next !== lastBoundUserName) {
            resetAppEventLogState();
        }
        lastBoundUserName = next;
    };
    sync();
    return store.subscribe(sync);
}

export function installRequestErrorCapture() {
    if (requestCaptureInstalled) {
        return;
    }

    requestInterceptorId = axios.interceptors.request.use((config: AxiosConfigWithMeta) => {
        config.metadata = {startedAt: Date.now()};
        return config;
    });

    responseInterceptorId = axios.interceptors.response.use(
        (response) => {
            logApiBusinessError(response);
            return response;
        },
        (error) => {
            logRequestError(error);
            return Promise.reject(error);
        }
    );

    requestCaptureInstalled = true;
}

export function installGlobalErrorCapture() {
    if (globalErrorCaptureInstalled || typeof window === 'undefined') {
        return;
    }

    window.addEventListener('error', (event) => {
        if (!event.message && !event.error) {
            return;
        }
        console.error('[unhandled-error]', {
            message: event.message,
            filename: event.filename,
            line: event.lineno,
            column: event.colno,
            error: event.error
        });
    });

    window.addEventListener('unhandledrejection', (event) => {
        if (isIgnorableRequestError(event.reason)) {
            return;
        }
        console.error('[unhandled-rejection]', event.reason);
    });

    globalErrorCaptureInstalled = true;
}

export function resetAppEventLogsForTests() {
    resetAppEventLogState();
    lastBoundUserName = '';
    if (requestInterceptorId != null) {
        axios.interceptors.request.eject(requestInterceptorId);
        requestInterceptorId = null;
    }
    if (responseInterceptorId != null) {
        axios.interceptors.response.eject(responseInterceptorId);
        responseInterceptorId = null;
    }
    requestCaptureInstalled = false;
    globalErrorCaptureInstalled = false;
}
